import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, Loader2, History } from "lucide-react";
import { api, coachHeaders, formatDateHeure } from "../lib/api";
import { RestoreDialog, RestoreFromFile } from "./RestoreDialog";

const ko = (n) => `${Math.max(1, Math.round((n ?? 0) / 1024))} Ko`;

export const BackupsPanel = ({ code }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cible, setCible] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/sauvegardes", coachHeaders(code)).then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, [code]);
  useEffect(() => { load(); }, [load]);
  const maintenant = async () => {
    setBusy(true);
    try { await api.post("/admin/sauvegardes", {}, coachHeaders(code)); toast.success("Sauvegarde faite"); load(); }
    catch { toast.error("Sauvegarde impossible"); }
    finally { setBusy(false); }
  };
  const telecharger = async (nom) => {
    try {
      const r = await api.get(`/admin/sauvegardes/${nom}`, { ...coachHeaders(code), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = nom;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Téléchargement impossible"); }
  };
  return (
    <div data-testid="backups-panel" className="rounded-2xl border border-line bg-pitch p-4">
      <h2 className="font-display text-2xl uppercase text-chalk">Sauvegardes</h2>
      <p className="mt-1 text-sm text-dim">Copie automatique chaque nuit vers 3h des joueurs, de l'encadrement, des convocations, réponses, archives et réglages. Les 30 dernières sont gardées sur le serveur.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="backup-now-button" onClick={maintenant} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-lime px-5 py-2.5 text-sm font-semibold text-ink disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />}Sauvegarder maintenant
        </button>
        <RestoreFromFile onPick={setCible} />
      </div>
      <ul className="mt-3 max-h-[24rem] divide-y divide-line overflow-y-auto">
        {rows.length === 0 && <li data-testid="backups-empty" className="py-3 text-sm text-dim">Aucune sauvegarde pour l'instant.</li>}
        {rows.map((b, i) => (
          <li key={b.nom} data-testid="backup-row" className="flex items-center justify-between gap-3 py-2 text-sm">
            <div className="min-w-0">
              <p className="text-chalk">{formatDateHeure(b.date)}{i === 0 && <span className="ml-2 text-xs text-lime">(dernière)</span>}</p>
              <p className="font-mono text-[11px] text-dim">{ko(b.taille)}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button data-testid={`backup-restore-${i}`} onClick={() => setCible(b)} aria-label="Restaurer"
                className="inline-flex items-center gap-1 rounded-full border border-signal px-3 py-1.5 text-xs font-semibold text-signal transition-colors hover:bg-signal hover:text-ink">
                <History className="h-3.5 w-3.5" />Restaurer
              </button>
              <button data-testid={`backup-download-${i}`} onClick={() => telecharger(b.nom)} aria-label="Télécharger"
                className="inline-flex items-center gap-1 rounded-full border border-lime px-3 py-1.5 text-xs font-semibold text-lime transition-colors hover:bg-lime hover:text-ink">
                <Download className="h-3.5 w-3.5" />Télécharger
              </button>
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-dim">Le fichier téléchargé contient des données personnelles : garde-le en lieu sûr.</p>
      {cible && <RestoreDialog code={code} cible={cible} onClose={() => setCible(null)} onDone={() => { setCible(null); setTimeout(() => window.location.reload(), 1200); }} />}
    </div>
  );
};
